import type { GameStateResponse, PublicRound } from "@kalaporssi/shared";
import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router";
import { Board } from "../components/Board";
import { CatchCard } from "../components/CatchCard";
import { ForecastTable } from "../components/ForecastTable";
import { ErrorBox, Loading } from "../components/Misc";
import { MarketTable, MonthResults, MonthTable, Standings } from "../components/Results";
import { api, readPlayerSession } from "../lib/api";
import { feur, monthName } from "../lib/format";
import { useAllResults, useGame, useResult } from "../lib/game";

/** Mobile-first player view (SPEC §12.3). */
export function Player() {
  const code = (useParams().code ?? "").toUpperCase();
  const session = readPlayerSession(code);
  const { state, connection, error, accept, refresh } = useGame(code);
  const meId = state?.me?.playerId;
  const me = state?.players.find((p) => p.id === meId);
  const round = state?.currentRound ?? null;
  const month = state?.status === "FINISHED" ? 12 : round ? round.month : -1;

  return (
    <>
      <Board month={month} players={state?.status === "LOBBY" ? undefined : state?.players} connection={connection} />
      <main className="stack">
        <ErrorBox error={error} />
        {!state && !error && <Loading />}
        {state && !session && (
          <div className="panel">
            <p>Tällä laitteella ei ole liitytty peliin {code}.</p>
            {state.status === "LOBBY" && (
              <Link className="btn tag" to={`/j/${code}`}>
                Liity peliin
              </Link>
            )}
          </div>
        )}
        {state && session && !me && (
          <p className="muted">Pelaajatunnistetta ei tunnistettu. Seuraat peliä katsojana.</p>
        )}
        {me && !me.active && (
          <div className="panel error">
            Pelinjohtaja poisti sinut pelistä. Voit seurata peliä, mutta et voi enää valita.
          </div>
        )}
        {state && (
          <PlayerBody state={state} meId={meId} active={me?.active ?? false} accept={accept} refresh={refresh} />
        )}
      </main>
    </>
  );
}

function PlayerBody(props: {
  state: GameStateResponse;
  meId: string | undefined;
  active: boolean;
  accept: (s: GameStateResponse) => void;
  refresh: () => void;
}) {
  const { state, meId } = props;
  const round = state.currentRound;
  switch (state.status) {
    case "LOBBY":
      return (
        <div className="stack">
          <div className="cover">
            <div className="big">Odotetaan pelinjohtajaa</div>
            <p className="muted">Peli alkaa, kun pelinjohtaja avaa tammikuun.</p>
          </div>
          <PeopleList state={state} meId={meId} />
        </div>
      );
    case "STARTED":
      return (
        <div className="cover">
          <div className="big">Peli alkaa</div>
          <p className="muted">Odotetaan, että pelinjohtaja avaa tammikuun.</p>
        </div>
      );
    case "ROUND_OPEN":
      return round ? <OpenRound code={state.code} round={round} {...props} /> : null;
    case "ROUND_RESOLVED":
      return round ? <Resolved state={state} round={round} meId={meId} /> : null;
    case "FINISHED":
      return <Final state={state} meId={meId} />;
  }
}

function PeopleList({ state, meId }: { state: GameStateResponse; meId?: string }) {
  return (
    <div className="panel">
      <h3>Pelaajat</h3>
      <div className="people" style={{ marginTop: 10 }}>
        {state.players
          .filter((p) => p.active)
          .map((p) => (
            <span key={p.id} className="st">
              {p.nickname}
              {p.type === "ai" && <span className="muted small">tekoäly</span>}
              {p.id === meId && <b className="small">sinä</b>}
            </span>
          ))}
      </div>
    </div>
  );
}

function OpenRound(props: {
  code: string;
  round: PublicRound;
  state: GameStateResponse;
  meId: string | undefined;
  active: boolean;
  refresh: () => void;
}) {
  const { round, code } = props;
  const [pending, setPending] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const dialog = useRef<HTMLDialogElement>(null);
  const answered = round.myChoice != null;

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset selection when a new round opens
  useEffect(() => {
    setPending(null);
    setError(null);
  }, [round.roundNo]);

  const confirm = async () => {
    if (pending == null) return;
    setBusy(true);
    setError(null);
    try {
      await api().submitChoice(code, round.roundNo, pending);
      dialog.current?.close();
      props.refresh();
    } catch (e) {
      setError(e);
      props.refresh();
    } finally {
      setBusy(false);
    }
  };

  const progress = (
    <div>
      <div className="row spread small">
        <span>
          {round.answered}/{round.expected} vastannut
        </span>
      </div>
      <div className="progress" aria-hidden="true">
        <div style={{ width: `${(round.answered / Math.max(1, round.expected)) * 100}%` }} />
      </div>
    </div>
  );

  if (answered || !props.active || !props.meId) {
    const mine = round.catches[round.myChoice ?? -1];
    return (
      <div className="stack">
        <div className="monthhead">
          <h2>{monthName(round.month)}</h2>
          <span className="muted">Kierros {round.roundNo}/12</span>
        </div>
        {mine && (
          <>
            <h3>Valintasi</h3>
            <div style={{ maxWidth: 320 }}>
              <CatchCard c={mine} mine />
            </div>
          </>
        )}
        <div className="panel stack">
          {answered && <p>Odotetaan muita. Pörssi aukeaa, kun kaikki ovat valinneet.</p>}
          {progress}
        </div>
      </div>
    );
  }

  const selected = pending == null ? null : round.catches[pending];
  return (
    <div className="stack">
      <div className="monthhead">
        <h2>{monthName(round.month)}</h2>
        <span className="muted">Kierros {round.roundNo}/12 · valitse yksi saalis</span>
      </div>
      <ForecastTable round={round} collapsible />
      <div className="grid">
        {round.catches.map((c) => (
          <CatchCard key={c.index} c={c} selected={pending === c.index} onSelect={() => setPending(c.index)} />
        ))}
      </div>
      <div className="row stickybar">
        <button
          type="button"
          className="btn tag"
          disabled={pending == null}
          onClick={() => dialog.current?.showModal()}
        >
          {pending == null ? "Valitse saalis" : `Valitse saalis #${pending + 1}`}
        </button>
      </div>
      {progress}
      <ErrorBox error={error} />
      <dialog ref={dialog} aria-labelledby="confirm-title">
        <div className="stack">
          <h3 id="confirm-title">Vahvista saalis #{selected?.number}</h3>
          {selected && <CatchCard c={selected} />}
          <p className="muted small">Valintaa ei voi muuttaa vahvistuksen jälkeen.</p>
          <ErrorBox error={error} />
          <div className="row">
            <button type="button" className="btn tag" onClick={confirm} disabled={busy}>
              {busy ? "Lähetetään…" : `Vahvista saalis #${selected?.number}`}
            </button>
            <button type="button" className="btn ghost" onClick={() => dialog.current?.close()} disabled={busy}>
              Peruuta
            </button>
          </div>
        </div>
      </dialog>
    </div>
  );
}

function Resolved({ state, round, meId }: { state: GameStateResponse; round: PublicRound; meId?: string }) {
  const { data: result, error } = useResult(state.code, round.roundNo);
  const mine = meId && result ? result.revenue[meId] : undefined;
  return (
    <div className="stack">
      <div className="monthhead">
        <h2>Pörssi: {monthName(round.month).toLowerCase()}</h2>
      </div>
      <ErrorBox error={error} />
      {!result && !error && <Loading />}
      {result && (
        <>
          {mine != null && (
            <div className="panel res me">
              <span className="muted">Tulosi tältä kuukaudelta</span>
              <div className="eur num">{feur(mine, 2)}</div>
            </div>
          )}
          <h3>Kuukauden tulos</h3>
          <MonthResults result={result} catches={round.catches} players={state.players} meId={meId} />
          <h3>Pörssitaulu</h3>
          <MarketTable result={result} />
          <h3>Kokonaistilanne</h3>
          <Standings players={state.players} meId={meId} />
          <p className="muted">
            {round.roundNo < 12 ? "Seuraavaa kuukautta odotetaan pelinjohtajalta." : "Peli päättyy."}
          </p>
        </>
      )}
    </div>
  );
}

export function Final({ state, meId, logLink = true }: { state: GameStateResponse; meId?: string; logLink?: boolean }) {
  const results = useAllResults(state.code, 12);
  const ranked = [...state.players].sort((a, b) => b.total - a.total);
  const winner = ranked[0];
  return (
    <div className="stack">
      <div className="monthhead">
        <h2>{winner?.nickname} voitti</h2>
        {winner && <span className="muted">{feur(winner.total)} vuoden aikana</span>}
      </div>
      <Standings players={state.players} meId={meId} />
      <MonthTable results={results.map((r) => r.data)} players={state.players} />
      {logLink && (
        <p>
          <Link className="btn tag" to={`/g/${state.code}/loki`}>
            Kapteenien lokikirja
          </Link>
        </p>
      )}
    </div>
  );
}
